import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MdDelete, MdEdit, MdSearch, MdInventory2, MdFilterList, MdClear } from "react-icons/md";
import { 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  IconButton, 
  TextField, 
  InputAdornment,
  Typography,
  Chip,
  Avatar,
  TablePagination,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  Stack
} from "@mui/material";

const ProductTable = ({ setEditMode, setAddProductMode, onDeleteProduct }) => {
  const dispatch = useDispatch();
  const selector = useSelector((state) => state.business);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);

  // Get unique categories
  const categories = useMemo(() => {
    if (!selector?.products) return [];
    const cats = new Set();
    selector.products.forEach(product => {
      if (product.category) cats.add(product.category);
    });
    return Array.from(cats).sort();
  }, [selector?.products]);

  const filteredProducts = useMemo(() => {
    if (!selector?.products) return [];
    
    let filtered = selector.products;

    // Apply search filter
    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(product => 
        product.product_name?.toLowerCase().includes(search) ||
        product.product_code?.toLowerCase().includes(search) ||
        product.category?.toLowerCase().includes(search) ||
        product.barcode?.toLowerCase().includes(search) ||
        product.description?.toLowerCase().includes(search)
      );
    }

    // Apply category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(product => product.category === categoryFilter);
    }

    // Apply stock filter
    if (stockFilter === 'in_stock') {
      filtered = filtered.filter(product => (product.stock_quantity || 0) > 0);
    } else if (stockFilter === 'low_stock') {
      filtered = filtered.filter(product => {
        const stock = product.stock_quantity || 0;
        return stock > 0 && stock < 10;
      });
    } else if (stockFilter === 'out_of_stock') {
      filtered = filtered.filter(product => (product.stock_quantity || 0) === 0);
    }

    return filtered;
  }, [selector?.products, searchText, categoryFilter, stockFilter]);

  // Pagination
  const paginatedProducts = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredProducts.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredProducts, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchText('');
    setCategoryFilter('all');
    setStockFilter('all');
    setPage(0);
  };

  const hasActiveFilters = searchText || categoryFilter !== 'all' || stockFilter !== 'all';

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };

  const getColorFromName = (name) => {
    const colors = [
      '#5c6ac4', '#8b5cf6', '#ec4899', '#f59e0b', 
      '#10b981', '#3b82f6', '#ef4444', '#6366f1'
    ];
    if (!name) return colors[0];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const getStockStatus = (stock) => {
    if (stock === 0) return { label: 'Out of Stock', color: 'error' };
    if (stock < 10) return { label: 'Low Stock', color: 'warning' };
    return { label: 'In Stock', color: 'success' };
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        border: '1px solid #e5e7eb', 
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}
    >
      {/* Search and Filter Bar */}
      <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search products by name, code, category, or barcode..."
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MdSearch style={{ color: '#6b7280' }} />
                </InputAdornment>
              ),
              endAdornment: searchText && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchText('');
                      setPage(0);
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <MdClear size={18} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                borderRadius: 1,
                '&:hover': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#d1d5db',
                  },
                },
                '&.Mui-focused': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#667eea',
                  },
                },
              },
            }}
          />

          {/* Category Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPage(0);
              }}
              sx={{
                backgroundColor: '#ffffff',
                borderRadius: 1,
              }}
            >
              <MenuItem value="all">All Categories</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Stock Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Stock Status</InputLabel>
            <Select
              value={stockFilter}
              label="Stock Status"
              onChange={(e) => {
                setStockFilter(e.target.value);
                setPage(0);
              }}
              sx={{
                backgroundColor: '#ffffff',
                borderRadius: 1,
              }}
            >
              <MenuItem value="all">All Stock</MenuItem>
              <MenuItem value="in_stock">In Stock</MenuItem>
              <MenuItem value="low_stock">Low Stock</MenuItem>
              <MenuItem value="out_of_stock">Out of Stock</MenuItem>
            </Select>
          </FormControl>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearFilters}
              startIcon={<MdClear />}
              sx={{
                textTransform: 'none',
                borderColor: '#d1d5db',
                color: '#6b7280',
                '&:hover': {
                  borderColor: '#9ca3af',
                  backgroundColor: '#f3f4f6',
                },
              }}
            >
              Clear
            </Button>
          )}
        </Stack>

        {/* Results Count */}
        <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Showing {paginatedProducts.length} of {filteredProducts.length} products
            {hasActiveFilters && ' (filtered)'}
          </Typography>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f9fafb' }}>
              <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Product</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#374151' }}>MRP</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Selling Price</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Stock</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#374151', textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedProducts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <MdInventory2 size={48} style={{ color: '#9ca3af' }} />
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters 
                        ? 'No products found matching your filters' 
                        : 'No products available'}
                    </Typography>
                    {hasActiveFilters && (
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleClearFilters}
                        sx={{ textTransform: 'none', mt: 1 }}
                      >
                        Clear all filters
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              paginatedProducts.map((product) => {
                const stockStatus = getStockStatus(product.stock_quantity || 0);
                return (
                  <TableRow
                    key={product.product_id}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#f9fafb',
                      },
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            bgcolor: getColorFromName(product.product_name),
                            width: 40,
                            height: 40,
                            fontSize: '0.875rem',
                          }}
                        >
                          {getInitials(product.product_name)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                            {product.product_name || 'N/A'}
                          </Typography>
                          {product.description && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {product.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {product.product_code || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {product.category ? (
                        <Chip
                          label={product.category}
                          size="small"
                          sx={{
                            backgroundColor: '#e0e7ff',
                            color: '#4338ca',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        ₹{parseFloat(product.mrp || 0).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>
                        ₹{parseFloat(product.selling_price || 0).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {product.stock_quantity || 0} {product.unit || 'PCS'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={stockStatus.label}
                        size="small"
                        color={stockStatus.color}
                        sx={{
                          fontWeight: 500,
                          fontSize: '0.75rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => setEditMode(product)}
                          sx={{
                            color: '#5c6ac4',
                            '&:hover': {
                              backgroundColor: 'rgba(92, 106, 196, 0.1)',
                            },
                          }}
                        >
                          <MdEdit size={18} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => onDeleteProduct(product.product_id)}
                          sx={{
                            color: '#ef4444',
                            '&:hover': {
                              backgroundColor: 'rgba(239, 68, 68, 0.1)',
                            },
                          }}
                        >
                          <MdDelete size={18} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={filteredProducts.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        sx={{
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          '& .MuiTablePagination-toolbar': {
            px: 2,
          },
          '& .MuiTablePagination-selectLabel': {
            fontSize: '0.875rem',
            color: '#6b7280',
          },
          '& .MuiTablePagination-displayedRows': {
            fontSize: '0.875rem',
            color: '#374151',
          },
          '& .MuiTablePagination-actions': {
            '& .MuiIconButton-root': {
              color: '#667eea',
              '&:hover': {
                backgroundColor: 'rgba(102, 126, 234, 0.1)',
              },
              '&.Mui-disabled': {
                color: '#d1d5db',
              },
            },
          },
        }}
      />
    </Paper>
  );
};

export default ProductTable;

